import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import tw from 'twin.macro';
import getMapLink from 'utils/getMapLink';
import CallIcon from 'components/icons/Call';
import MailIcon from 'components/icons/Mail';
import MapMarkerIcon from 'components/icons/MapMarker';
import HoursPopup from './HoursPopup';
import { ScheduleAnAppointmentModal } from 'components/components/molecules';
import { useUiValue } from 'stores/uiContext';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';

const Pill = styled.button`
  ${tw`inline-flex items-center bg-transparent cursor-pointer justify-center font-graphie text-xs font-medium tracking-wider text-white h-8 rounded-md transition-colors`}
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  text-decoration: none;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.6);
  }

  ${({ $iconOnly }) => ($iconOnly ? tw`px-1.5 flex-shrink-0` : tw`px-3`)}
  ${({ $grow }) => $grow && tw`flex-1 lg:flex-none`}
`;

const buildDirectionsHref = (sc) => {
  if (!sc) return null;
  if (sc.gmbLink && sc.gmbLink.href) return sc.gmbLink.href;
  if (sc.latitude && sc.longitude) {
    return getMapLink(`${sc.latitude},${sc.longitude}`);
  }
  const parts = [
    sc.address1,
    sc.address2,
    sc.city,
    sc.stateAbbreviation,
    sc.zipcode,
  ]
    .filter(Boolean)
    .join(' ');
  return parts ? getMapLink(parts) : null;
};

const ContactBarSalesCenter = ({ sc = {}, calendlyModal }) => {
  const hoursBtnRef = useRef(null);
  const [hoursOpen, setHoursOpen] = useState(false);

  const directionsHref = buildDirectionsHref(sc);
  const cityStateZip = [
    sc.city,
    sc.stateAbbreviation && sc.zipcode
      ? `${sc.stateAbbreviation} ${sc.zipcode}`
      : sc.stateAbbreviation || sc.zipcode,
  ]
    .filter(Boolean)
    .join(', ');
  const [{ hasScheduleAppointmentCTA }] = useUiValue();
  const [isScheduleAppointmentModalOpen, setIsScheduleAppointmentModalOpen] =
    useState(false);
  return (
    <>
      {hasScheduleAppointmentCTA && sc.scheduleAppointment && calendlyModal && (
        <ScheduleAnAppointmentModal
          isOpen={isScheduleAppointmentModalOpen}
          onClose={() => setIsScheduleAppointmentModalOpen(false)}
          fields={calendlyModal}
        />
      )}
      {sc.title && (
        <div className='font-trade-gothic-20 text-base font-semibold tracking-widest uppercase text-white mb-1'>
          {sc.title}
        </div>
      )}

      <div className='font-graphie text-sm leading-relaxed text-white mb-3'>
        {sc.address1 && (
          <div>
            {sc.address1}
            {sc.address2 ? ` ${sc.address2}` : ''}
            {cityStateZip ? `, ${cityStateZip}` : ''}
          </div>
        )}
        {sc.phoneNumberCall && (
          <div className='font-bold'>{sc.phoneNumberCall}</div>
        )}
      </div>

      <div className='flex flex-row flex-wrap items-center gap-2'>
        {sc.phoneNumberCall && (
          <Pill
            as='a'
            href={`tel:${sc.phoneNumberCall.replace(/[^0-9+]/g, '')}`}
            $iconOnly
            aria-label={`Call ${sc.phoneNumberCall}`}
          >
            <CallIcon className='w-4 h-4' />
          </Pill>
        )}

        {sc.email && (
          <Pill
            as='a'
            href={`mailto:${sc.email}`}
            $iconOnly
            aria-label={`Email ${sc.email}`}
          >
            <MailIcon className='w-4 h-4' />
          </Pill>
        )}

        {sc.schedule && sc.schedule.length > 0 && (
          <Pill
            ref={hoursBtnRef}
            type='button'
            onClick={() => setHoursOpen((v) => !v)}
            aria-expanded={hoursOpen}
            aria-haspopup='dialog'
          >
            {sc.scheduleTitle || 'Hours'}
          </Pill>
        )}

        {directionsHref && (
          <Pill
            as='a'
            href={directionsHref}
            onClick={() => {
              trackAnalyticsEvent({
                gtagEvent: 'CommunityDirectionsClick',
                mouseflowTag: 'CommunityDirectionsClick',
              });
            }}
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Get directions to sales center, opens in new tab'
            $grow
          >
            <MapMarkerIcon className='w-4 h-4' />
            <span className='ml-1'>Directions</span>
          </Pill>
        )}

        {sc.scheduleAppointment && calendlyModal && (
          <Pill
            type='button'
            aria-haspopup='true'
            onClick={() => {
              trackAnalyticsEvent({
                //dataLayerEvent: 'schedule-appointment',
                gtagEvent: 'ScheduleApptClick',
                mouseflowTag: 'ScheduleAppt',
              });
              setIsScheduleAppointmentModalOpen(true);
            }}
            $grow
          >
            {sc.scheduleAppointment}
          </Pill>
        )}
      </div>

      {sc.schedule && sc.schedule.length > 0 && hoursOpen && (
        <HoursPopup
          open={hoursOpen}
          anchorRef={hoursBtnRef}
          schedule={sc.schedule}
          onClose={() => setHoursOpen(false)}
        />
      )}
    </>
  );
};

ContactBarSalesCenter.propTypes = {
  salesCenterVM: PropTypes.shape({
    title: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    stateAbbreviation: PropTypes.string,
    zipcode: PropTypes.string,
    phoneNumberCall: PropTypes.string,
    email: PropTypes.string,
    schedule: PropTypes.array,
    scheduleTitle: PropTypes.string,
    scheduleAppointment: PropTypes.string,
    gmbLink: PropTypes.shape({ href: PropTypes.string }),
    latitude: PropTypes.string,
    longitude: PropTypes.string,
  }),
};

export default ContactBarSalesCenter;
